/**
 * BaseComponent — Lifecycle base class for all UI components in an EduTool.
 *
 * Contract:
 *   - Always constructed with a root HTMLElement and a ComponentContext
 *   - init() is called exactly once before any other method
 *   - show() / hide() toggle visibility without destroying the component
 *   - applyRole() reacts to user role changes (docente/studente/display)
 *   - dispose() cleans up listeners, timers, references
 *
 * Subclasses implement the protected hooks (onInit, onShow, onHide,
 * onRoleChange, onDispose) and must NOT override the public methods.
 */

import type { EduToolBridge } from '@/bridge/edutool-bridge';
import type { Locale, Theme, UserRole } from '@/bridge/bridge-types';
import type { I18nService } from '@/i18n/I18nService';
import type { EventBus } from '@/core/EventBus';
import { ComponentError } from '@/core/errors';

export interface ComponentContext {
  role: UserRole;
  locale: Locale;
  theme: Theme;
  bridge: EduToolBridge;
  i18n: I18nService;
  bus: EventBus;
}

export abstract class BaseComponent {
  protected readonly element: HTMLElement;
  protected context: ComponentContext;
  protected initialized = false;
  protected visible = false;
  private readonly cleanupFns: Array<() => void> = [];

  constructor(element: HTMLElement, context: ComponentContext) {
    this.element = element;
    this.context = context;
  }

  async init(): Promise<void> {
    if (this.initialized) return;
    await this.onInit();

    // Auto-subscribe to locale changes: call render() if subclass defines it
    const unsubscribe = this.context.i18n.onChange(() => {
      this.onLocaleChange(this.context.i18n.getLocale());
    });
    this.addCleanup(unsubscribe);

    this.initialized = true;
  }

  show(): void {
    if (!this.initialized) {
      throw new ComponentError(this.constructor.name, 'show() called before init()');
    }
    this.element.removeAttribute('hidden');
    this.visible = true;
    this.onShow();
  }

  hide(): void {
    this.element.setAttribute('hidden', '');
    this.visible = false;
    this.onHide();
  }

  isVisible(): boolean {
    return this.visible;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getElement(): HTMLElement {
    return this.element;
  }

  applyRole(role: UserRole): void {
    this.context.role = role;
    this.onRoleChange(role);
  }

  applyTheme(theme: Theme): void {
    this.context.theme = theme;
    this.onThemeChange(theme);
  }

  dispose(): void {
    this.cleanupFns.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.error(`[${this.constructor.name}] cleanup error:`, err);
      }
    });
    this.cleanupFns.length = 0;
    this.onDispose();
    this.initialized = false;
  }

  /**
   * Register a cleanup function to be called on dispose.
   * Use for event listeners, timers, subscriptions.
   */
  protected addCleanup(fn: () => void): void {
    this.cleanupFns.push(fn);
  }

  /**
   * Helper: add an event listener and automatically register its cleanup.
   */
  protected addEventListener<K extends keyof HTMLElementEventMap>(
    target: EventTarget,
    event: K | string,
    handler: EventListener,
    options?: AddEventListenerOptions,
  ): void {
    target.addEventListener(event, handler, options);
    this.addCleanup(() => target.removeEventListener(event, handler, options));
  }

  /* ============================================================
   * Hooks for subclasses
   * ============================================================ */

  protected abstract onInit(): Promise<void>;

  protected onShow(): void {}
  protected onHide(): void {}
  protected onRoleChange(_role: UserRole): void {}
  protected onThemeChange(_theme: Theme): void {}
  protected onLocaleChange(_locale: Locale): void {}
  protected onDispose(): void {}
}
